/**
 * User settings, as `medecine360-bff` exposes them on
 * `GET/PUT /users/{id}/settings` (`UserSettingsResponse` / `UserSettingsRequest`).
 *
 * `theme`, `timeZone` and `visibility` are validated as NOT NULL by both the
 * BFF (`@ValidTheme`/`@ValidTimeZone`/`@ValidVisibility` all reject null) and
 * the API (`UserSettingsDto`), and the PUT *replaces* every field. The admin
 * renders none of the three — nothing in this app reads a theme, an American
 * timezone or a directory visibility — so they are read on GET and written
 * straight back on PUT, never surfaced as controls.
 */
export type SettingsTheme = "LIGHT" | "DARK" | "SYSTEM";

export type SettingsTimeZone =
  | "UTC"
  | "EASTERN_TIME"
  | "CENTERAL_TIME"
  | "MONTAIN_TIME"
  | "PACIFIC_TIME";

export type SettingsVisibility =
  | "PUBLIC"
  | "PRIVATE"
  | "DOCTORS_ONLY"
  | "REGISTERED_USERS_ONLY";

/** `LanguageEnum` on both backends. The admin only ships FR and EN catalogues. */
export type SettingsLanguage = "FR" | "EN" | "WO" | "AR";

/** `NotificationEnum` — the categories a user can opt in or out of. */
export enum NotificationCategory {
  APPOINTMENTS = "APPOINTMENTS",
  MESSAGES = "MESSAGES",
  REVIEWS = "REVIEWS",
  UPDATES = "UPDATES",
  REMINDERS = "REMINDERS",
  MARKETING = "MARKETING",
}

export const NOTIFICATION_CATEGORIES = Object.values(NotificationCategory);

export type UserSettings = {
  settingsId?: number | null;
  theme?: SettingsTheme | null;
  timeZone?: SettingsTimeZone | null;
  visibility?: SettingsVisibility | null;
  appLanguage?: SettingsLanguage | null;
  notifications?: NotificationCategory[] | null;
  userId?: number | null;
};

/** Body of `PUT /users/{id}/settings`. Every field the endpoint requires. */
export type UpdateUserSettingsPayload = {
  settingsId?: number | null;
  theme: SettingsTheme;
  timeZone: SettingsTimeZone;
  visibility: SettingsVisibility;
  appLanguage: SettingsLanguage;
  notifications: NotificationCategory[];
  userId?: number | null;
};
